import { useEffect, useState } from 'react';

import { currentBranding } from '../../../config/branding';
import { RoleListInterFace } from '../../../interface/role-management.interface';
import { UserInterFace } from '../../../interface/user-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchFacilityTagsList } from '../../../redux/slice/FacilityTagSlice';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import { fetchAllRolesWithOutPage } from '../../../redux/slice/RoleManagementSlice';
import { fetchAllUsers } from '../../../redux/slice/UserManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomPhoneNumber from '../../../shared-components/custom-phone-number-input/CustomPhoneNumberInput';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import MultiSelectDropdown from '../../../shared-components/multi-select-field/MultiSelectField';
import SelectFiled from '../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotificaion';
import {
  emailValidation,
  stringValidation,
  passwordValidation,
  phoneNumberValidation,
} from '../../../shared-functions/Validation';
import userApi from '../../../utils/api/users';

interface Option {
  label: string;
  value: string;
}

interface UserDetailsInterface {
  role: string;
  name: string;
  email: string;
  confirmPassword: string;
  password: string;
  phoneNumber: string;
  countryCode: string;
  facilities?: string[];
}
interface AddUserProperties {
  handleCancel: () => void;
  currentData?: UserInterFace;
}

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/**
 * Add User function component
 */
function AddUser(properties: AddUserProperties) {
  const { handleCancel } = properties;
  const dispatch = useAppDispatch();
  const { page, limit } = useAppSelector((state) => state.users);
  const { facilityTagsList } = useAppSelector((state) => state.facilityTags);
  const facilityTagsListOption: Option[] = facilityTagsList.map((facilityTag) => {
    return {
      label: facilityTag.facilityTagName,
      value: facilityTag._id,
    };
  });

  const { roleList, loading } = useAppSelector((state) => state.roles);

  const [formStatus, setFormStatus] = useState({
    showFormErrors: false,
    hint: true,
    buttonLoader: false,
  });
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
  });

  const [userDetails, setUserDetails] = useState<UserDetailsInterface>({
    role: '',
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    countryCode: currentBranding === 'energic' ? '966' : '1',
    facilities: [],
  });

  /**
   * Handle the fetch data function
   */
  const fetchData = () => {
    if (!loading) {
      dispatch(fetchAllRolesWithOutPage());
    }
  };

  /**
   * Handle the station detail change
   */
  const handleUserDetailChange = (name: string, value: string | number | (string | number)[]) => {
    setUserDetails((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Handle the form submit function
   */
  const submit = () => {
    setFormStatus({
      showFormErrors: true,
      hint: false,
      buttonLoader: false,
    });
    if (
      !stringValidation(1, 100, userDetails.name) ||
      userDetails.role === '' ||
      !emailValidation(userDetails.email) ||
      !phoneNumberValidation(userDetails.phoneNumber) ||
      !passwordValidation(userDetails.password) ||
      userDetails.confirmPassword !== userDetails.password
    ) {
      return;
    }

    setFormStatus({
      showFormErrors: true,
      hint: false,
      buttonLoader: true,
    });
    const request = {
      name: userDetails.name,
      role: userDetails.role,
      email: userDetails.email,
      password: userDetails.password,
      phoneNumber: userDetails.phoneNumber,
      facilities: userDetails.facilities,
      countryCode: userDetails.countryCode,
    };

    userApi
      .createUser(request)
      .then(() => {
        dispatch(fetchAllUsers({ page, limit }));
        setFormStatus({
          showFormErrors: false,
          hint: false,
          buttonLoader: false,
        });
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: null,
          content: (
            <SuccessNotification
              title="User created successfully"
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={handleCancel}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error) => {
        setFormStatus({
          showFormErrors: false,
          hint: false,
          buttonLoader: false,
        });
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  useEffect(() => {
    if (roleList.length === 0) {
      fetchData();
    }
    dispatch(fetchFacilityTagsList());
  }, []);

  return (
    <div className="add-user-content">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      <div className="multi-select">
        <SelectFiled
          inputLabel="Role"
          name="role"
          placeholder="Select role"
          selectedValue={userDetails.role}
          onChangeValue={handleUserDetailChange}
          options={roleList.map((role: RoleListInterFace) => ({
            label: role.name,
            value: role._id,
          }))}
          valueRequired
          error={formStatus.showFormErrors && userDetails.role === ''}
          errorMessage="User role is required"
        />
      </div>
      <div className="form-field">
        <MultiSelectDropdown
          placeholderFilled="Facility"
          title="Select Facility"
          options={facilityTagsListOption}
          name="facilities"
          placeholder="Select Facility"
          search
          selectedValues={userDetails.facilities || []}
          onSelectionChange={handleUserDetailChange}
        />
      </div>
      <div className="add-user-name-input">
        <CustomInput
          inputLabel="Name"
          type="text"
          placeholder="Enter the name"
          name="name"
          value={userDetails.name}
          onChangeValue={handleUserDetailChange}
          valueRequired
          error={formStatus.showFormErrors && !stringValidation(1, 100, userDetails.name)}
          errorMessage="User name is required and Name length maximum 100"
        />
      </div>
      <div className="add-user-name-input">
        <CustomInput
          inputLabel="Email"
          type="text"
          placeholder="Enter email"
          name="email"
          value={userDetails.email}
          onChangeValue={handleUserDetailChange}
          valueRequired
          error={formStatus.showFormErrors && !emailValidation(userDetails.email)}
          errorMessage="Invalid email"
        />
      </div>
      <div className="add-user-name-input">
        <CustomPhoneNumber
          inputLabel="Phone number"
          placeholder="Enter phone number"
          name="phoneNumber"
          value={userDetails.phoneNumber}
          countryCode={userDetails.countryCode}
          onChangeValue={handleUserDetailChange}
          valueRequired
          error={formStatus.showFormErrors && !phoneNumberValidation(userDetails.phoneNumber)}
          errorMessage="Invalid phone number"
        />
      </div>
      <div className="add-user-name-input">
        <CustomInput
          inputLabel="Set Password"
          type="password"
          placeholder="Enter password"
          name="password"
          value={userDetails.password}
          onChangeValue={handleUserDetailChange}
          valueRequired
          error={formStatus.showFormErrors && !passwordValidation(userDetails.password)}
          errorMessage="Password must be at least 12 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character( ! @ # $ % ^ & * ( ) _ + [ \ ] { } ; &apos; : &quot; \\ | , . < > / ? ~ - )."
          hint={userDetails.password.length === 0 || !passwordValidation(userDetails.password) ? formStatus.hint : false}
          hintMessage='Password must be 12+ characters, with at least 1 uppercase, 1 lowercase, 1 number, and 1 special character( ! @ # $ % ^ & * ( ) _ + [ \ ] { } ; &apos; : &quot; \\ | , . < > / ? ~ - ).'
        />
      </div>
      <div className="add-user-name-input">
        <CustomInput
          inputLabel="Re-Enter-Password"
          type="password"
          placeholder="Enter password"
          name="confirmPassword"
          value={userDetails.confirmPassword}
          onChangeValue={handleUserDetailChange}
          valueRequired
          error={
            formStatus.showFormErrors &&
            (userDetails.password !== userDetails.confirmPassword ||
              userDetails.confirmPassword === '')
          }
          errorMessage="Password & Confirm password should be same."
        />
      </div>
      <div className="user-form-footer">
        <MyButton label="Cancel" buttonType="back" onClickFunc={handleCancel} />
        <MyButton
          label="Submit"
          buttonType="submit"
          onClickFunc={submit}
          disableButton={formStatus.buttonLoader}
          loading={formStatus.buttonLoader}
        />
      </div>
    </div>
  );
}

export default AddUser;
